import { Injectable } from '@angular/core';
import { Store } from 'rxjs-observable-store';
import { Observable, Subscription, throwError } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, filter, takeWhile, tap } from 'rxjs/operators';
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import { NavigationEnd, Router } from '@angular/router';

import { ToursSearch } from 'package-types';

import { SearchResultState } from './search-result.state';
import { ToursSearchService } from '@services/tours-search/tours-search.service';
import { InformationStore } from '@services/store/information.store';
import { QueryParamsService } from '@services/query-params.service';
import { createDefaultSearchWithDestination } from '@helpers/search.helper';
import { getStoreRequestStateUpdater } from '@helpers/endpoint.helper';
import { PlatformService } from '@services/platform.service';
import { SearchErrorsService } from '@services/tours-search/search-errors.service';
import { SearchFormStore } from '@services/store/search-form.store';

import { ParsedParams } from '@interfaces/parsed-params';
import { SearchParams } from '@interfaces/modules/tours/search';
import { ToursSearchResult } from '@interfaces/tours-search-result';
import { StoreRequestStateUpdater } from '@interfaces/helpers/store-request-state-updater';
import { defaultToursSearchSort, ToursSearchSort } from '@interfaces/services/api/tours-search.endpoint';

import ResultStatus = ToursSearch.ResultStatus;

@UntilDestroy()
@Injectable()
export class SearchResultStore extends Store<SearchResultState> {
  private readonly searchResultPageSize = 40;
  private readonly storeRequestStateUpdater: StoreRequestStateUpdater;
  private departureCitiesSubscription?: Subscription;

  constructor(
    private readonly searchService: ToursSearchService,
    private readonly informationStore: InformationStore,
    private readonly queryParamsService: QueryParamsService,
    private readonly searchForm: SearchFormStore,
    private readonly platform: PlatformService,
    private readonly searchErrorsService: SearchErrorsService,
    private readonly router: Router
  ) {
    super(new SearchResultState());
    this.storeRequestStateUpdater = getStoreRequestStateUpdater(this);
  }

  init(): void {
    this.reloadSearchDueToSearchParams();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => this.reloadSearchDueToSearchParams());
  }

  loadHotels(page = 1, sort: ToursSearchSort = this.state.sort, keepHotels = false): void {
    const { search, searchId } = this.state.actualSearch;
    this.departureCitiesSubscription?.unsubscribe();
    if (page === 1) {
      this.setState({
        ...this.state,
        page,
        sort,
        status: undefined,
        hotels: keepHotels ? this.state.hotels : [],
        hasNext: true,
        isSorting: keepHotels
      });
    } else {
      this.setState({ ...this.state, page, sort, status: undefined, isSorting: keepHotels });
    }
    this.setState({ ...this.state, departureCitiesMessage: undefined });
    if (!searchId && this.platform.isBrowser()) {
      const locationId = search.destination?.countryId ?? search.destination?.id;
      this.departureCitiesSubscription = this.searchForm.state$
        .pipe(
          filter(state => state.departureCitiesStatus !== 'idle'),
          takeWhile(state => !(state.departureCitiesLocationId === locationId
            && state.departureCitiesStatus === 'ready'
            && search.departureCity
            && state.departureCities.some(city => city.id === search.departureCity)), true),
          untilDestroyed(this)
        )
        .subscribe(({ departureCities, departureCitiesStatus, departureCitiesLocationId }) => {
          if (departureCitiesLocationId !== locationId) {
            this.stopForDepartureCities('DepartureCityRequired');
          } else if (departureCitiesStatus === 'loading') {
            this.setState({ ...this.state, departureCitiesMessage: undefined, status: undefined, hasNext: false });
          } else if (departureCitiesStatus === 'error') {
            this.stopForDepartureCities('DepartureCitiesLoadingFailure');
          } else if (!departureCities.length) {
            this.stopForDepartureCities('NoDepartureCities');
          } else if (!search.departureCity || !departureCities.some(city => city.id === search.departureCity)) {
            this.stopForDepartureCities('DepartureCityRequired');
          } else {
            this.loadSearchResult(search, searchId, page, sort);
          }
        });

      return;
    }
    this.loadSearchResult(search, searchId, page, sort);
  }

  retryDepartureCities(): void {
    const { destination } = this.state.actualSearch.search;
    if (this.searchForm.state.departureCitiesLocationId !== (destination?.countryId ?? destination?.id)) {
      this.stopForDepartureCities('DepartureCityRequired');

      return;
    }
    this.departureCitiesSubscription?.unsubscribe();
    this.searchForm.retryDepartureCities();
    this.loadHotels(1);
  }

  private stopForDepartureCities(message: SearchResultState['departureCitiesMessage']): void {
    this.setState({
      ...this.state,
      departureCitiesMessage: message,
      status: ResultStatus.Ready,
      hotels: [],
      hasNext: false,
      isSorting: false,
      requests: { ...this.state.requests, search: { inProgress: false, success: false, error: false } }
    });
  }

  private loadSearchResult(search: SearchParams, searchId: string | undefined, page: number, sort: ToursSearchSort): void {
    this.receiveResult(search, searchId, page, sort)
      .pipe(
        filter(v => this.isResultVisible(v)),
        untilDestroyed(this)
      )
      .subscribe({
        next: ({ id, res: { status, result: { hotels, totalCount } } }) => {
          this.setState({
            ...this.state,
            actualSearch: { ...this.state.actualSearch, searchId: id },
            status,
            hotels,
            hasNext: this.hasNextPage(page, status, hotels, totalCount),
            isSorting: false
          });
        },
        error: (err) => {
          this.setState({ ...this.state, isSorting: false });
          this.searchErrorsService.handleSearchErrors(err, search);
        }
      });
  }

  loadNextPage(): void {
    this.loadHotels(this.state.page + 1, this.state.sort);
  }

  changeSort(sort: ToursSearchSort): void {
    if (sort === this.state.sort) {
      return;
    }

    this.queryParamsService.patchParams({ sort });
    this.searchService.finishActiveSearches();
    this.loadHotels(Math.max(this.state.page, 1), sort, true);
  }

  private reloadSearchDueToSearchParams() {
    const { search, searchId, sort } = this.buildActualSearchParams(this.queryParamsService.getParsedParamsSnapshot());
    if (this.isCurrentSearch(search, searchId)) {
      return;
    }
    this.departureCitiesSubscription?.unsubscribe();
    this.searchForm.setSearchParams(search);
    this.reloadHotels(search, searchId, sort);
  }

  private reloadHotels(search: SearchParams, searchId?: string, sort: ToursSearchSort = this.state.sort || defaultToursSearchSort): void {
    this.setState({ ...this.state, actualSearch: { searchId, search }, sort, isSorting: false });
    this.searchService.finishActiveSearches();
    this.loadHotels(1, sort);
  }

  private receiveResult(search: SearchParams, searchId?: string, page?: number, sort: ToursSearchSort = defaultToursSearchSort): Observable<ToursSearchResult> {
    const updater = this.storeRequestStateUpdater.bind(null, 'search');
    updater({ inProgress: true });

    return this.searchService.search(search, searchId, page, sort)
      .pipe(
        tap(() => updater({ inProgress: false, success: true })),
        catchError((err) => {
          updater({ inProgress: false, error: true });

          return throwError(err);
        })
      );
  }

  private isResultVisible({ res: { status, result } }: ToursSearchResult): boolean {
    return status === ResultStatus.Ready
      || status === ResultStatus.Failed
      || Boolean(result?.hotels?.length);
  }

  private hasNextPage(
    page: number,
    status: ResultStatus | undefined,
    hotels: ToursSearchResult['res']['result']['hotels'],
    totalCount: number
  ): boolean {
    if (status === ResultStatus.Partial || status === ResultStatus.InProgress) {
      return true;
    }

    return hotels.length >= page * this.searchResultPageSize && hotels.length < totalCount;
  }

  private isCurrentSearch(search: SearchParams, searchId?: string): boolean {
    if (!Object.prototype.hasOwnProperty.call(this.state, 'actualSearch')) {
      return false;
    }
    const actualSearch = (this.state as SearchResultState & { actualSearch?: SearchResultState['actualSearch'] }).actualSearch;

    return Boolean(actualSearch)
      && actualSearch.searchId === searchId
      && isEqual(actualSearch.search, search);
  }

  private buildActualSearchParams({ searchId, search = { }, sort = defaultToursSearchSort }: ParsedParams): { search: SearchParams, searchId?: string, sort: ToursSearchSort } {
    const { destination, ...searchWithoutDestination } = search;
    const newSearch = merge(createDefaultSearchWithDestination(this.informationStore.state.pageInfo), searchWithoutDestination);

    return { searchId, search: newSearch, sort };
  }
}
